import React, { useEffect, useMemo, useState } from 'react';

interface DashboardWidgetProps {
    apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
    currentTenantId: number | null;
}

interface MonthlyData {
    year: number;
    month: number;
    income: number;
    expense: number;
    net: number;
}

const formatCurrency = (amount: number): string => {
    return `¥${new Intl.NumberFormat('ja-JP').format(amount)}`;
};

const DashboardWidget: React.FC<DashboardWidgetProps> = ({ apiFetch, currentTenantId }) => {
    const [data, setData] = useState<MonthlyData[]>([]);
    const [loading, setLoading] = useState(false);

    const getCurrentFiscalYear = () => {
        const now = new Date();
        return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    };

    useEffect(() => {
        if (!currentTenantId) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                const year = getCurrentFiscalYear();
                // 4月から現在の月（または年度末）まで取得
                const months = [];
                for (let i = 0; i < 12; i++) {
                    const m = (i + 3) % 12 + 1;
                    const y = m >= 4 ? year : year + 1;
                    const start = `${y}-${String(m).padStart(2, '0')}-01`;
                    const end = `${y}-${String(m).padStart(2, '0')}-${new Date(y, m, 0).getDate()}`;
                    months.push({ year: y, month: m, start, end });
                }

                const promises = months.map(async (m) => {
                    const res = await apiFetch(`/api/cash-flow-statement?period_start=${m.start}&period_end=${m.end}`);
                    if (res.ok) {
                        const json = await res.json();
                        return {
                            year: m.year,
                            month: m.month,
                            income: json.total_income || 0,
                            expense: json.total_expense || 0,
                            net: json.net_cash_flow || 0
                        };
                    }
                    return { year: m.year, month: m.month, income: 0, expense: 0, net: 0 };
                });

                const results = await Promise.all(promises);
                setData(results);
            } catch (e) {
                console.error("Dashboard fetch error", e);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentTenantId]);

    if (!currentTenantId) {
        return (
            <div style={{ backgroundColor: 'white', border: '1px solid #ccc', padding: '15px', marginBottom: '20px', borderRadius: '4px', textAlign: 'center', color: '#666' }}>
                <div style={{ fontSize: '14px', marginBottom: '8px' }}>🏢 保育園（テナント）が選択されていません</div>
                <div style={{ fontSize: '12px' }}>経営ダッシュボードを表示するには、画面上部から対象の保育園を選択してください。</div>
            </div>
        );
    }

    const maxVal = Math.max(...data.map(d => Math.max(d.income, d.expense)), 1);

    return (
        <div style={{ backgroundColor: 'white', border: '1px solid #ccc', padding: '15px', marginBottom: '20px', borderRadius: '4px' }}>
            <h3 style={{ marginTop: 0, fontSize: '14px', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
                経営ダッシュボード ({getCurrentFiscalYear()}年度 資金収支)
            </h3>
            {loading ? (
                <div style={{ fontSize: '12px', color: '#666' }}>ロード中...</div>
            ) : (
                <div style={{ display: 'flex', overflowX: 'auto', gap: '10px', paddingTop: '10px' }}>
                    {data.map((d) => (
                        <div key={`${d.year}-${d.month}`} style={{ minWidth: '60px', textAlign: 'center', fontSize: '10px' }}>
                            <div style={{ height: '100px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '2px' }}>
                                <div
                                    title={`収入: ${formatCurrency(d.income)}`}
                                    style={{
                                        width: '10px',
                                        height: `${(d.income / maxVal) * 100}%`,
                                        backgroundColor: '#4caf50',
                                        minHeight: d.income > 0 ? '1px' : '0'
                                    }}
                                />
                                <div
                                    title={`支出: ${formatCurrency(d.expense)}`}
                                    style={{
                                        width: '10px',
                                        height: `${(d.expense / maxVal) * 100}%`,
                                        backgroundColor: '#f44336',
                                        minHeight: d.expense > 0 ? '1px' : '0'
                                    }}
                                />
                            </div>
                            <div style={{ marginTop: '4px', borderTop: '1px solid #ccc' }}>{d.month}月</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default DashboardWidget;
